import { Injectable } from '@angular/core';
import { WindowRef } from '@spartacus/core';

const KN_BR_CART_CONTEXT_STORAGE_KEY = 'kn-br-cart-context';

@Injectable({
  providedIn: 'root',
})
export class KnBrCartContextAdapter {
  constructor(private winRef: WindowRef) {}

  private getCurrentCustomerNumber(): string | null {
    if (!this.winRef.localStorage) {
      return null;
    }

    const customerContext = this.winRef.localStorage.getItem('kn-br-customer-context');
    if (!customerContext) {
      return null;
    }

    try {
      const parsed = JSON.parse(customerContext);
      return parsed?.customerNumber || null;
    } catch (e) {
      return null;
    }
  }

  private getStorageData() {
    if (!this.winRef.localStorage) {
      return { activeCart: null, cartsByCustomer: {} };
    }

    const raw = this.winRef.localStorage.getItem(KN_BR_CART_CONTEXT_STORAGE_KEY);
    if (!raw) {
      return { activeCart: null, cartsByCustomer: {} };
    }

    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'string') {
        return { activeCart: parsed, cartsByCustomer: {} };
      }

      // If stored as object with per-customer carts, migrate to legacy single-string format.
      const parsedActive = parsed?.activeCart ?? null;
      const parsedMap = parsed?.cartsByCustomer ?? {};
      const hasMap = parsedMap && Object.keys(parsedMap).length > 0;

      if (!parsedActive && hasMap) {
        // Prefer cart for current customer if available, otherwise pick first entry.
        const currentCustomer = this.getCurrentCustomerNumber();
        let chosen: string | null = null;
        if (currentCustomer && parsedMap[currentCustomer]) {
          chosen = parsedMap[currentCustomer];
        } else {
          const firstKey = Object.keys(parsedMap)[0];
          chosen = parsedMap[firstKey] ?? null;
        }
        // Persist legacy string format so other code sees the simple value.
        this.saveStorageData(chosen);
        return { activeCart: chosen, cartsByCustomer: {} };
      }

      return {
        activeCart: parsedActive,
        cartsByCustomer: parsedMap,
      };
    } catch (e) {
      // If the stored value is not JSON, treat it as the legacy string cart id
      return { activeCart: raw, cartsByCustomer: {} };
    }
  }

  private saveStorageData(data: string | { activeCart: string | null; cartsByCustomer: Record<string, string> }) {
    if (!this.winRef.localStorage) {
      return;
    }
    if (data === null || data === undefined) {
      this.winRef.localStorage.removeItem(KN_BR_CART_CONTEXT_STORAGE_KEY);
      return;
    }
    if (typeof data === 'string') {
      this.winRef.localStorage.setItem(KN_BR_CART_CONTEXT_STORAGE_KEY, data);
    } else {
      this.winRef.localStorage.setItem(KN_BR_CART_CONTEXT_STORAGE_KEY, JSON.stringify(data));
    }
  }

  get() {
    const data = this.getStorageData();
    return data.activeCart ?? null;
  }

  getForCustomer(customerNumber: string) {
    if (!customerNumber) {
      return null;
    }

    const data = this.getStorageData();
    // This app fetches carts from server; return the stored active cart if present.
    return data.activeCart ?? data.cartsByCustomer?.[customerNumber] ?? null;
  }

  set(context: string) {
    if (!this.winRef.localStorage) {
      return;
    }
    // Store legacy simple string cart id in localStorage so other flows can read it
    // directly. If null/undefined provided, remove the storage key.
    this.saveStorageData(context ?? null);
  }

  setForCustomer(customerNumber: string, context: string) {
    if (!customerNumber) {
      return;
    }
    // For backward-compatibility write the cart id as the simple active cart value.
    this.saveStorageData(context ?? null);
  }

  remove() {
    if (!this.winRef.localStorage) {
      return;
    }

    // Remove the stored cart context entirely (legacy behavior expects
    // the key to be absent rather than a JSON object with nulls).
    this.saveStorageData(null);
  }

  removeForCustomer(customerNumber: string) {
    if (!customerNumber) {
      return;
    }

    const data = this.getStorageData();
    delete data.cartsByCustomer[customerNumber];
    const hasActive = !!data.activeCart;
    const hasAnyCustomer = Object.keys(data.cartsByCustomer || {}).length > 0;
    if (!hasActive && !hasAnyCustomer) {
      // nothing left to store
      this.saveStorageData(null);
    } else {
      this.saveStorageData(data);
    }
  }
}
